import 'temporal-polyfill/global';

/**
 * Coerces supported date inputs to a valid Date, or undefined when invalid.
 */
function coerceToDate(value?: Date | string | number): Date | undefined {
  if (value == null) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value === 'number' || typeof value === 'string') {
    if (typeof value === 'string' && value.length === 0) {
      return undefined;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  return undefined;
}

export function formatDateTimeISO(
  value?: Date | string | number,
  options?: globalThis.Intl.DateTimeFormatOptions,
): string {
  const date = coerceToDate(value);
  if (!date) return '';

  const instant = Temporal.Instant.from(date.toISOString());
  // Get the system's local time zone string
  const systemZoneId = Temporal.Now.zonedDateTimeISO().timeZoneId;
  const zoned = instant.toZonedDateTimeISO(systemZoneId);
  // Format as a medium date/time string
  return zoned.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...options,
  });
}

/**
 * Formats a date for display. When `options` is omitted, uses `{ dateStyle: 'short' }`.
 * When `options` is provided, it is passed through as-is (do not mix `dateStyle` with
 * field keys like `month`/`day` in the same options object).
 */
export function formatDateISO(
  value?: Date | string | number,
  options?: globalThis.Intl.DateTimeFormatOptions,
): string {
  const date = coerceToDate(value);
  if (!date) return '';

  const instant = Temporal.Instant.from(date.toISOString());
  // Get the system's local time zone string
  const systemZoneId = Temporal.Now.zonedDateTimeISO().timeZoneId;
  const zoned = instant.toZonedDateTimeISO(systemZoneId);
  return zoned.toLocaleString(undefined, options ?? { dateStyle: 'short' });
}

export function formatTimeISO(
  value?: Date | string | number,
  options?: globalThis.Intl.DateTimeFormatOptions,
): string {
  const date = coerceToDate(value);
  if (!date) return '';

  const instant = Temporal.Instant.from(date.toISOString());
  // Get the system's local time zone string
  const systemZoneId = Temporal.Now.zonedDateTimeISO().timeZoneId;
  const zoned = instant.toZonedDateTimeISO(systemZoneId);
  // Format as a medium date/time string
  return zoned.toLocaleString(undefined, {
    timeStyle: 'short',
    ...options,
  });
}

/**
 * Formats a non-negative millisecond duration as `mm:ss` (minutes may exceed 99).
 * Returns an empty string for null, undefined, NaN, or negative values.
 */
export function formatMilliseconds(ms?: number): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '';
  if (ms === 0) return '00:00';

  const duration = Temporal.Duration.from({ milliseconds: ms });
  const totalSeconds = duration.total({ unit: 'seconds' });
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
